import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroCanvas from "@/components/HeroCanvas";
import { scrollToId, goToTab } from "@/lib/lenis";

const H1_LINES = [
  { text: "AI Handles the Rule", italic: false, accentDot: true },
  { text: "Humans Handle the Exception", italic: false, accentDot: true },
  { text: "We Engineer the Handoff", italic: true, accentDot: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const assessment = () => {
    goToTab("assessment");
    scrollToId("#contact");
  };
  const consultation = () => {
    goToTab("consultation");
    scrollToId("#contact");
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-20">
      <motion.div style={{ y: gridY }} className="grid-lines pointer-events-none absolute inset-0" />
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute inset-0">
        <div
          className="absolute right-[-10%] top-[-20%] h-[70vh] w-[70vh] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(235,94,40,0.07) 0%, rgba(235,94,40,0) 65%)" }}
        />
        <div
          className="absolute bottom-[-30%] left-[-15%] h-[60vh] w-[60vh] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,139,155,0.05) 0%, rgba(0,139,155,0) 65%)" }}
        />
      </motion.div>

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 pb-20 pt-14 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-28 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#E2E8F0] bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#626D7D] shadow-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#EB5E28]" />
            Human-in-the-Loop Architecture
          </motion.p>

          <h1
            data-testid="hero-main-heading"
            className="font-display text-4xl font-medium leading-[1.08] tracking-[-0.03em] text-[#0F141A] sm:text-5xl lg:text-[4.25rem]"
          >
            {H1_LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.italic ? "italic" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                  {line.accentDot && <span className="not-italic text-[#EB5E28]">.</span>}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-[#3B434E] sm:text-lg"
          >
            Scaling your business with AI requires more than just software. When automated workflows
            hit an unpredictable edge case, they break. We design{" "}
            <span className="font-semibold text-[#0F141A]">Human-in-the-Loop (HITL)</span>{" "}
            architecture to ensure your automated systems are mathematically precise, and your
            people are empowered to manage the exceptions seamlessly.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <button
              data-testid="hero-hitl-assessment-btn"
              onClick={assessment}
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#EB5E28] px-8 py-4 text-sm font-semibold text-white shadow-md shadow-[#EB5E28]/20 transition-colors duration-200 hover:bg-[#D94E1B]"
            >
              Schedule a HITL Workflow Assessment
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-consultation-secondary-btn"
              onClick={consultation}
              className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-[#CBD5E1] bg-white px-8 py-4 text-sm font-medium text-[#0F141A] shadow-sm transition-colors duration-200 hover:border-[#0F141A]"
            >
              Book a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8B95A5]"
          >
            Rule · Exception · Handoff
          </motion.p>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto aspect-square max-w-[480px]"
          >
            <HeroCanvas />
            <div className="float-slow absolute -left-2 top-8 rounded-full border border-[#E2E8F0] bg-white/85 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-[#626D7D] shadow-sm backdrop-blur">
              RULE · AUTOMATED
            </div>
            <div
              className="float-slow absolute bottom-10 right-0 rounded-full border border-[#EB5E28]/30 bg-white/85 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-[#EB5E28] shadow-sm backdrop-blur"
              style={{ animationDelay: "1.6s" }}
            >
              EXCEPTION · HUMAN
            </div>
          </motion.div>
        </div>
      </div>

      <div className="relative mx-auto hidden max-w-[1440px] items-center gap-4 px-12 pb-10 lg:flex">
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#8B95A5]">Scroll</span>
        <span className="scroll-line relative h-px w-16 overflow-hidden bg-[#E2E8F0]" />
      </div>
    </section>
  );
}
